import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { RefreshCw, Sparkles } from "lucide-react";

const currency = (n, code = "CHF") =>
  new Intl.NumberFormat("de-CH", { style: "currency", currency: code, maximumFractionDigits: 0 }).format(n || 0);

export default function DashboardOverview() {
  const { lang } = useLang();
  const [ov, setOv] = useState(null);
  const [health, setHealth] = useState(null);
  const [briefing, setBriefing] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/overview").then((r) => setOv(r.data)),
      api.get("/guardian/health").then((r) => setHealth(r.data)),
    ]).catch(() => {});
  }, []);

  const runBriefing = useCallback(async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/ai/briefing", { module_id: "ceo", language: lang });
      setBriefing(data.reply);
    } catch (e) {
      setBriefing("Unable to generate briefing right now.");
    } finally {
      setGenBusy(false);
    }
  }, [lang]);

  useEffect(() => {
    runBriefing();
  }, [runBriefing]);

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display font-light text-3xl tracking-tight">
            {t(lang, "overview_title")}
          </h1>
          <p className="mt-1 text-white/50 text-sm">{t(lang, "overview_target")}</p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
          {ov?.phase || "Phase 1"}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard testid="kpi-revenue" label={t(lang, "overview_kpi_revenue")} value={currency(ov?.revenue_today_chf)} />
        <KpiCard testid="kpi-profit" label={t(lang, "overview_kpi_profit")} value={currency(ov?.net_profit_today_chf)} accent />
        <KpiCard testid="kpi-orders" label={t(lang, "overview_kpi_orders")} value={ov?.orders_today ?? "—"} />
        <KpiCard testid="kpi-trust" label={t(lang, "overview_kpi_autonomy")} value={ov ? `${Math.round((ov.autonomous_resolution_rate || 0) * 100)}%` : "—"} sub={ov ? `target ${Math.round((ov.autonomous_resolution_target || 0.85) * 100)}%` : undefined} />
      </div>

      {/* Secondary strip */}
      <div className="grid gap-3 md:grid-cols-3">
        <div className="hq-card p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
            {t(lang, "overview_modules_active")}
          </div>
          <div className="font-mono text-2xl">
            {ov?.modules_active ?? "—"}
            <span className="text-white/40 text-sm ml-2">/ {ov?.modules_total ?? 12}</span>
          </div>
        </div>
        <div className="hq-card p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
            {t(lang, "overview_guardian")}
          </div>
          <div className="flex items-center gap-2">
            <span className={`hq-dot hq-dot-${health?.overall || "cyan"}`}></span>
            <span className="font-mono text-xl uppercase">{health?.overall || "…"}</span>
          </div>
        </div>
        <div className="hq-card p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
            Audit events
          </div>
          <div className="font-mono text-2xl">{ov?.audit_events_total ?? "—"}</div>
        </div>
      </div>

      {/* CEO AI briefing */}
      <div className="hq-card p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[color:var(--hq-cyan)]/40 to-transparent"></div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-[color:var(--hq-cyan)]" />
            <div>
              <div className="font-display text-lg">{t(lang, "overview_briefing")}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                {t(lang, "overview_briefing_hint")}
              </div>
            </div>
          </div>
          <button
            data-testid="run-briefing-btn"
            onClick={runBriefing}
            disabled={genBusy}
            className="hq-btn hq-btn-cyan disabled:opacity-50"
          >
            <RefreshCw size={12} className={genBusy ? "animate-spin" : ""} />
            {t(lang, "overview_run_briefing")}
          </button>
        </div>
        <div
          data-testid="briefing-content"
          className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap min-h-[80px]"
        >
          {genBusy && !briefing ? (
            <span className="text-white/40">…</span>
          ) : (
            briefing || <span className="text-white/40">—</span>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent = false, testid, sub }) {
  return (
    <div className="hq-card p-5" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">
        {label}
      </div>
      <div className={`font-mono text-3xl ${accent ? "text-[color:var(--hq-cyan)]" : "text-white"}`}>
        {value}
      </div>
      {sub && (
        <div className="mt-2 font-mono text-[10px] uppercase text-white/40">{sub}</div>
      )}
    </div>
  );
}
